import React from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useState, useContext, useEffect } from 'react';
import { GlobalContext } from '../context/GlobalState';

// MUI's default typography asks for Roboto, which this app never loads, so these controls silently
// fall back to Helvetica - whose heaviest face is Bold. Any weight above 700 would render as plain
// bold. This is the same stack `body` already uses in index.css, and San Francisco / Segoe UI both
// carry a Semibold and a Heavy face, so the height anchors below can actually be told apart.
const SYSTEM_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif';

// Weights for the height dropdown's anchor rows. The tens carry the list; the fives are half-steps.
const ANCHOR_WEIGHT_TEN = 1000;
const ANCHOR_WEIGHT_FIVE = 1000;
const WEIGHT_REGULAR = 400;

export default function Dropdown({
  attributeType,
  attributeLabel,
  values,
  attributesArray,
  setAttributesArrayFunc,
  preselectValue
}) {
  const [attribute, setAttribute] = useState('');
  const { lang } = useContext(GlobalContext);

  values.sort((a, b) => a.position - b.position);

  // The height dropdown lists every centimetre (see utils/heightUtils.js), so it is a long, uniform
  // column of numbers. Weighting each multiple of five gives the eye somewhere to land while
  // scanning, with the tens heavier than the fives so the list keeps a readable rhythm.
  const heightAnchorWeight = (value) => {
    if (attributeType !== 'height') {
      return WEIGHT_REGULAR;
    }
    const cm = Number(value);
    if (!Number.isFinite(cm) || cm === 0) {
      return WEIGHT_REGULAR;
    }
    if (cm % 10 === 0) {
      return ANCHOR_WEIGHT_TEN;
    }
    return cm % 5 === 0 ? ANCHOR_WEIGHT_FIVE : WEIGHT_REGULAR;
  };

  // Apply a chosen option: update the visible Select and push it up to the parent's attributesArray
  // (which triggers the variant-price fetch once every dimension group has been chosen).
  const applySelection = (selectedAttributeValue) => {
    setAttribute(selectedAttributeValue);

    const selectedAttribute = values.find((val) => val.value === selectedAttributeValue);
    if (!selectedAttribute) {
      return;
    }

    // Copy, never mutate. `attributesArray` is handed straight to a cart line when the customer
    // adds to cart (ProductViewGridItemAction), so writing into it here would silently rewrite the
    // size of an item already in the cart. Likewise `selectedAttribute` belongs to the `values`
    // options list - stamping a label onto it would edit the dropdown's own data.
    setAttributesArrayFunc({
      ...attributesArray,
      [attributeType]: { ...selectedAttribute, label: attributeLabel }
    });
  };

  const handleAttributeChange = (event) => {
    applySelection(event.target.value);
  };

  // Preselect from a URL-provided value (e.g. ?width=180) once the options are available and nothing
  // has been chosen yet. Matches by string so it works for integer (width/length), text (height) and
  // combined-string (size) dimension values.
  useEffect(() => {
    if (preselectValue == null || preselectValue === '' || attribute !== '') {
      return;
    }
    const match = values.find((val) => String(val.value) === String(preselectValue));
    if (match) {
      applySelection(match.value);
    }
  }, [preselectValue, values]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ p: 1 }}>
        <FormControl fullWidth>
          <InputLabel
            id="demo-simple-select-label"
            sx={{
              fontFamily: SYSTEM_FONT_STACK,
              color: 'text.primary',
              '&.Mui-focused': {
                color: 'gold.text'
              }
            }}>
            {attributeLabel}
          </InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={attribute}
            label={attributeLabel}
            onChange={handleAttributeChange}
            MenuProps={{
              PaperProps: {
                sx: {
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#c9a227 #f4efe0',
                  '&::-webkit-scrollbar': {
                    // macOS draws an overlay scrollbar that fades out once you stop scrolling, so the
                    // gold would only ever flash by. Opting out of the native appearance gives us a
                    // classic bar that stays drawn for as long as the list can scroll.
                    WebkitAppearance: 'none',
                    width: '8px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f4efe0',
                    borderRadius: '4px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c9a227',
                    borderRadius: '4px'
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a8871f'
                  }
                }
              }
            }}
            sx={{
              fontFamily: SYSTEM_FONT_STACK,
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'divider'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'gold.fill'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'gold.fill'
              }
            }}>
            {values.map((attr) => (
              <MenuItem
                key={attr.value}
                value={attr.value}
                sx={{
                  fontFamily: SYSTEM_FONT_STACK,
                  fontWeight: heightAnchorWeight(attr.value)
                }}>
                {attr.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}
